import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import '../styles/Page.css';
import '../styles/JobDetail.css';

const hostURL = config.serverUrl;

function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Application form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    website: '',
    linkedin: '',
    usWorkAuth: '',
    requiresSponsorship: '',
    gender: '',
    hispanicLatino: '',
    veteranStatus: '',
    disabilityStatus: ''
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [coverLetterFile, setCoverLetterFile] = useState(null);

  useEffect(() => {
    fetchJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${hostURL}/api/jobs/${id}`);
      setJob(response.data.job);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching job:', err);
      setError('Job not found or has expired');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatCompensation = (min, max) => {
    if (!min && !max) return 'Compensation not specified';
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return `${formatter.format(min)} - ${formatter.format(max)}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (fileType === 'resume') {
      setResumeFile(file);
    } else if (fileType === 'coverLetter') {
      setCoverLetterFile(file);
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccess('');
    setSubmitting(true);

    // Validate required fields
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'usWorkAuth', 'requiresSponsorship', 'gender', 'hispanicLatino', 'veteranStatus', 'disabilityStatus'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      setFormError('Please fill out all required fields');
      setSubmitting(false);
      document.querySelector('.application-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Validate resume is uploaded
    if (!resumeFile) {
      setFormError('Please upload your resume/CV');
      setSubmitting(false);
      document.querySelector('.application-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    try {
      // Create FormData to send files
      const formDataToSend = new FormData();
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      
      // Append files
      if (resumeFile) {
        formDataToSend.append('resume', resumeFile);
      }
      if (coverLetterFile) {
        formDataToSend.append('coverLetter', coverLetterFile);
      }

      await axios.post(
        `${hostURL}/api/jobs/${id}/apply`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSuccess('Application submitted successfully! We will review your application and contact you soon.');
      
      // Reset form on successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        website: '',
        linkedin: '',
        usWorkAuth: '',
        requiresSponsorship: '',
        gender: '',
        hispanicLatino: '',
        veteranStatus: '',
        disabilityStatus: ''
      });
      setResumeFile(null);
      setCoverLetterFile(null);
      
      // Clear file input fields
      const resumeInput = document.querySelector('input[type="file"][accept=".pdf,.doc,.docx"]');
      const coverLetterInput = document.querySelectorAll('input[type="file"][accept=".pdf,.doc,.docx"]')[1];
      if (resumeInput) resumeInput.value = '';
      if (coverLetterInput) coverLetterInput.value = '';
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccess('');
      }, 5000);
      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting application:', error);
      setFormError(error.response?.data?.error || 'Failed to submit application. Please try again.');
      document.querySelector('.application-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading job details...</div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Job Not Found</h1>
        </div>
        <div className="page-content">
          <div className="error-message">{error}</div>
          <button className="back-button" onClick={() => navigate('/careers')}>
            Back to Careers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-content">
        <div className="job-detail-container">
          <section className="job-section">
            <h1>{job.title}</h1>
            <div className="job-meta">
              <span className="job-work-model-badge">{job.work_model}</span>
              <span className="job-expiration-text">Expires: {formatDate(job.expiration)}</span>
              <span className="job-location-text">{job.location}</span>
            </div>
            <h2>Job Description</h2>
            <div className="job-content">
              {job.content.split('\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
            <button type="button" className="back-button" onClick={() => navigate('/careers')}>
                Back to All Positions
            </button>
          </section>

          <section className="job-section">
            <h2>Compensation</h2>
            <p className="compensation-range">
              {formatCompensation(job.compensation_min, job.compensation_max)}
            </p>
          </section>

          <section className="job-section application-section">
            <h2>Apply for this Position</h2>
            
            {success && <div className="success-message">{success}</div>}
            {formError && <div className="error-message">{formError}</div>}
            
            <form onSubmit={handleSubmitApplication} className="application-form">
              {/* Personal Information */}
              <div className="form-section">
                <h3>Personal Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://"
                  />
                </div>
                
                <div className="form-group">
                  <label>LinkedIn Profile</label>
                  <input
                    type="url"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleInputChange}
                    placeholder="https://linkedin.com/in/"
                  />
                </div>
              </div>

              {/* Documents */}
              <div className="form-section">
                <h3>Documents</h3>
                <div className="form-group">
                  <label>Resume/CV*</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange(e, 'resume')}
                  />
                  {resumeFile && <span className="file-name">{resumeFile.name}</span>}
                </div>
                
                <div className="form-group">
                  <label>Cover Letter</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileChange(e, 'coverLetter')}
                  />
                  {coverLetterFile && <span className="file-name">{coverLetterFile.name}</span>}
                </div>
              </div>

              {/* Work Authorization */}
              <div className="form-section">
                <h3>Work Authorization</h3>
                <div className="form-group">
                  <label>Are you authorized to work in the United States? *</label>
                  <select
                    name="usWorkAuth"
                    value={formData.usWorkAuth}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Will you require sponsorship from OneAligned Inc. for employment now or in the future (e.g., H1B visa)? *</label>
                  <select
                    name="requiresSponsorship"
                    value={formData.requiresSponsorship}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              </div>

              {/* Voluntary Self-Identification */}
              <div className="form-section voluntary-section">
                <h3>Voluntary Self-Identification</h3>
                <p className="voluntary-intro">
                  For government reporting purposes, we ask candidates to respond to the below self-identification survey. 
                  Completion of the form is entirely voluntary. Whatever your decision, it will not be considered in the hiring 
                  process or thereafter. Any information that you do provide will be recorded and maintained in a confidential file.
                </p>
                <p className="voluntary-intro">
                  As set forth in OneAligned Inc.'s Equal Employment Opportunity policy, we do not discriminate on the basis of 
                  any protected group status under any applicable law.
                </p>
                
                <div className="form-group">
                  <label>Gender *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Decline To Self Identify">Decline To Self Identify</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Are you Hispanic/Latino? *</label>
                  <select
                    name="hispanicLatino"
                    value={formData.hispanicLatino}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Decline To Self Identify">Decline To Self Identify</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Veteran Status *</label>
                  <p className="field-description">
                    If you believe you belong to any of the categories of protected veterans, please indicate by making the appropriate selection. 
                    As a government contractor subject to the Vietnam Era Veterans Readjustment Assistance Act (VEVRAA), we request this information 
                    in order to measure the effectiveness of the outreach and positive recruitment efforts we undertake pursuant to VEVRAA.
                  </p>
                  <select
                    name="veteranStatus"
                    value={formData.veteranStatus}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="I am not a protected veteran">I am not a protected veteran</option>
                    <option value="I identify as one or more of the classifications of a protected veteran">I identify as one or more of the classifications of a protected veteran</option>
                    <option value="I do not wish to answer">I do not wish to answer</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Disability Status *</label>
                  <p className="field-description">
                    We are a federal contractor or subcontractor. The law requires us to provide equal employment opportunity to qualified people with disabilities. 
                    We have a goal of having at least 7% of our workers as people with disabilities. Completing this form is voluntary, and we hope that you will choose to do so. 
                    Your answer is confidential. No one who makes hiring decisions will see it.
                  </p>
                  <select
                    name="disabilityStatus"
                    value={formData.disabilityStatus}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="Yes, I have a disability, or have had one in the past">Yes, I have a disability, or have had one in the past</option>
                    <option value="No, I do not have a disability, and have not had one in the past">No, I do not have a disability, and have not had one in the past</option>
                    <option value="I do not want to answer">I do not want to answer</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-button" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}

export default JobDetail;
